"use client";

import { useState, useEffect } from "react";
import { useSession, signIn } from "next-auth/react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Bot,
  Sparkles,
  Zap,
  CheckCircle,
  Loader2,
  PenTool,
} from "lucide-react";
import Image from "next/image";
import { toast } from "sonner";

interface AILoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess?: () => void;
  feature?: "chat" | "homework" | "research" | "blog";
  redirectUrl?: string;
}

const featureConfig = {
  chat: {
    title: "Access AI Chat Assistant",
    description:
      "Sign in to start conversing with our intelligent learning assistant and save your chat history",
    icon: <Bot className="h-8 w-8 text-blue-600" />,
    benefits: [
      "Save conversation history",
      "Personalized learning experience",
      "Context-aware responses",
      "24/7 learning support",
    ],
    color: "from-blue-600 to-cyan-600",
  },
  homework: {
    title: "Access Homework Helper",
    description:
      "Sign in to get step-by-step guidance for your assignments and track your progress",
    icon: <Sparkles className="h-8 w-8 text-orange-600" />,
    benefits: [
      "Step-by-step problem solving",
      "Subject-specific assistance",
      "Learning progress tracking",
      "Educational guidance",
    ],
    color: "from-orange-600 to-yellow-600",
  },
  research: {
    title: "Access Research Assistant",
    description:
      "Sign in to get comprehensive research support and save your research sessions",
    icon: <Zap className="h-8 w-8 text-purple-600" />,
    benefits: [
      "Comprehensive research guidance",
      "Multiple depth levels",
      "Citation assistance",
      "Academic writing support",
    ],
    color: "from-purple-600 to-pink-600",
  },
  blog: {
    title: "Access Blog Creation",
    description:
      "Sign in with admin privileges to create and publish blog posts for the school community",
    icon: <PenTool className="h-8 w-8 text-green-600" />,
    benefits: [
      "Create rich content blog posts",
      "Share knowledge with community",
      "Professional blog editor",
      "Admin access required",
    ],
    color: "from-green-600 to-emerald-600",
  },
};

export function AILoginModal({
  isOpen,
  onClose,
  onLoginSuccess,
  feature = "chat",
  redirectUrl,
}: AILoginModalProps) {
  const { data: session, status } = useSession();
  const [isLoading, setIsLoading] = useState(false);
  const config = featureConfig[feature];

  // Close modal if user becomes authenticated
  useEffect(() => {
    if (status === "authenticated" && session) {
      onLoginSuccess?.();
      onClose();
      toast.success("Welcome! You can now access all AI features.");
    }
  }, [status, session, onLoginSuccess, onClose]);

  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true);

      // Determine callback URL
      const callbackUrl =
        redirectUrl ||
        (typeof window !== "undefined" ? window.location.href : "/ai");

      // Use the same approach as the working register page
      await signIn("google", {
        callbackUrl,
        redirect: true, // Let NextAuth handle the redirect like in register
      });

      toast.success("Redirecting to Google...");
    } catch (error) {
      console.error("Sign in failed:", error);
      toast.error("Sign in failed. Please try again.");
      setIsLoading(false);
    }
    // Don't set loading false here since we're redirecting
  };

  // Don't render if user is already authenticated
  if (status === "authenticated") {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md mx-auto">
        <DialogHeader className="text-center space-y-4">
          <div
            className={`mx-auto w-16 h-16 rounded-full bg-gradient-to-br ${config.color} flex items-center justify-center`}
          >
            {config.icon}
          </div>
          <DialogTitle className="text-2xl font-bold">
            {config.title}
          </DialogTitle>
          <DialogDescription className="text-base">
            {config.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Benefits Section */}
          <Card className="border-none shadow-sm bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                What you get with sign in:
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <ul className="space-y-2">
                {config.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center text-sm">
                    <div className="h-1.5 w-1.5 rounded-full bg-green-500 mr-2 flex-shrink-0" />
                    {benefit}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Google Sign In Button */}
          <Button
            onClick={handleGoogleSignIn}
            disabled={isLoading || status === "loading"}
            className="w-full h-12 text-base font-semibold bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 shadow-sm"
            variant="outline"
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin mr-3" />
            ) : (
              <svg className="h-5 w-5 mr-3" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
            )}
            {isLoading ? "Signing in..." : "Continue with Google"}
          </Button>

          {/* Security Note */}
          <div className="text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Secure sign-in powered by Google OAuth. <br />
              Your information is protected and never shared.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
